import { normName } from "@/lib/promo";
import type { CustomerGroup, ResellerGroup } from "@/lib/reseller-groups";

// ─── DISKON PER MEREK PER LEVEL (mig 7070) ───────────────────────────────────
//
// Aturan `reseller_group_brand_discounts`: untuk pembeli level G, produk
// bermerek X berharga umum × (1 − persen/100). Aturan itu MENGGANTIKAN override
// per-varian slot G dan diskon % rata G (keputusan pemilik 2026-10-08: "aturan
// merek menang"); promo kampanye tetap di atasnya seperti terhadap level biasa.
//
// Berkas ini helper MURNI (tanpa Supabase): bentuk baris RPC
// `group_brand_discount_rules()` → peta per level, pencocokan merek produk, dan
// validasi draf editor admin. Rumus harganya sendiri hidup di pricing.ts — di
// sini hanya "aturan mana yang berlaku", bukan "berapa harganya".
//
// Merek dicocokkan lewat NAMA ternormalisasi (`normName`, cermin SQL
// `lower(btrim())` di `max_group_brand_discount_for_product`) karena
// `products.brand` adalah teks bebas; aturan diikat ke `product_brands.id` di DB
// sehingga ganti nama merek (admin_rename_brand) tak memutus aturan.

/** Baris mentah RPC `group_brand_discount_rules()` (numeric bisa datang sebagai string). */
export type GroupBrandDiscountRow = {
  group_id: string;
  group_slot: number | string;
  brand_id: string;
  brand_name: string;
  brand_active: boolean | null;
  discount_persen: number | string;
};

/** Satu aturan yang sudah dibersihkan. */
export type GroupBrandRule = {
  groupId: string;
  slot: number;
  brandId: string;
  brandName: string;
  brandActive: boolean;
  persen: number;
};

/** Nama merek ternormalisasi → persen. Kosong = level tanpa aturan merek. */
export type BrandDiscountMap = ReadonlyMap<string, number>;

export const EMPTY_BRAND_DISCOUNTS: BrandDiscountMap = new Map();

/**
 * Persen yang SAH untuk sebuah aturan: 0 < v ≤ 100, paling banyak dua desimal —
 * cermin CHECK `reseller_group_brand_discounts_persen_chk` + validasi RPC tulis.
 */
export function isUsableBrandPersen(value: number): boolean {
  if (!Number.isFinite(value) || value <= 0 || value > 100) return false;
  return Math.abs(value * 100 - Math.round(value * 100)) < 1e-9;
}

/** Baris RPC → aturan; baris rusak (persen mustahil, id kosong) dibuang, tak ditebak. */
export function groupBrandRuleFromRow(row: GroupBrandDiscountRow): GroupBrandRule | null {
  const persen = Number(row.discount_persen);
  const slot = Number(row.group_slot);
  if (!row.group_id || !row.brand_id || !isUsableBrandPersen(persen) || !Number.isFinite(slot)) {
    return null;
  }
  return {
    groupId: row.group_id,
    slot,
    brandId: row.brand_id,
    brandName: row.brand_name ?? "",
    brandActive: row.brand_active !== false,
    persen
  };
}

export function groupBrandRulesFromRows(
  rows: readonly GroupBrandDiscountRow[] | null | undefined
): GroupBrandRule[] {
  const out: GroupBrandRule[] = [];
  for (const row of rows ?? []) {
    const rule = groupBrandRuleFromRow(row);
    if (rule) out.push(rule);
  }
  return out;
}

/**
 * Peta aturan per level. Dua merek berbeda id yang namanya sama sesudah
 * dinormalkan (RPC tulis menolaknya, tapi data lama/impor bisa saja) → yang
 * TERBESAR menang — cermin `max(discount_persen)` helper lantai SQL, supaya
 * harga yang dihitung tak pernah di bawah lantai yang diterima server.
 */
export function brandDiscountsByGroup(
  rules: readonly GroupBrandRule[]
): Map<string, BrandDiscountMap> {
  const byGroup = new Map<string, Map<string, number>>();
  for (const rule of rules) {
    const key = normName(rule.brandName);
    if (!key) continue;
    let map = byGroup.get(rule.groupId);
    if (!map) {
      map = new Map();
      byGroup.set(rule.groupId, map);
    }
    const prev = map.get(key);
    if (prev === undefined || rule.persen > prev) map.set(key, rule.persen);
  }
  return byGroup;
}

/** Peta aturan SATU level (kosong bila level itu tanpa aturan). */
export function brandDiscountsForGroup(
  rules: readonly GroupBrandRule[],
  groupId: string | null | undefined
): BrandDiscountMap {
  if (!groupId) return EMPTY_BRAND_DISCOUNTS;
  return brandDiscountsByGroup(rules.filter((r) => r.groupId === groupId)).get(groupId) ??
    EMPTY_BRAND_DISCOUNTS;
}

/**
 * Persen aturan merek untuk satu produk, atau `null` bila tak ada. Produk Non
 * Web (`isManual`) tak pernah kena — harganya diketik admin per pembeli
 * (cermin `not is_manual` di helper lantai SQL).
 */
export function brandDiscountFor(
  map: BrandDiscountMap | null | undefined,
  product: { brand?: string | null; isManual?: boolean | null } | null | undefined
): number | null {
  if (!map || map.size === 0 || !product || product.isManual) return null;
  const key = normName(product.brand);
  if (!key) return null;
  return map.get(key) ?? null;
}

/**
 * Pasang aturan merek level ke objek grup (pemuat: CustomerContext, checkout,
 * keranjang admin, kasir, pengisi harga). Grup null tetap null.
 *
 * Grup NONAKTIF (`isActive === false`) mendapat peta KOSONG — keputusan fitur
 * (`docs/PUTUSAN-DISKON-MEREK.md`): level nonaktif tak memakai aturan mereknya,
 * cermin lantai SQL (`g.is_active = true`, mig 7070) dan `groupFromRow` yang
 * membuang grup nonaktif di storefront/checkout. Sebelumnya jalur admin yang
 * membangun grup apa adanya (konteks edit PO, Tambah Produk, Edit Variasi)
 * tetap memotong merek untuk level nonaktif sementara katalog menagih harga
 * umum (bughunt D13-5). Diskon RATA level nonaktif di jalur admin sengaja
 * tidak diubah di sini (diterima mig 7080).
 */
export function withBrandDiscounts(
  group: CustomerGroup,
  map: BrandDiscountMap | null | undefined
): CustomerGroup {
  if (!group) return null;
  if (group.isActive === false) return { ...group, brandDiscounts: EMPTY_BRAND_DISCOUNTS };
  return { ...group, brandDiscounts: map ?? EMPTY_BRAND_DISCOUNTS };
}

/**
 * Buang aturan milik grup NONAKTIF dari daftar aturan toko (bughunt D13-5).
 * Untuk pemanggil yang grupnya dibangun TANPA tahu status aktif — Kasir
 * memaksa `isActive: true` karena `customer_summary` tak membawa status grup.
 * Grup yang tak tercantum di `groups` dianggap aktif (tak ada bukti nonaktif).
 */
export function rulesOfActiveGroups(
  rules: readonly GroupBrandRule[],
  groups: ReadonlyArray<{ id: string; is_active?: boolean | null }>
): GroupBrandRule[] {
  const inactive = new Set(groups.filter((g) => g.is_active === false).map((g) => g.id));
  return rules.filter((r) => !inactive.has(r.groupId));
}

/**
 * GRUP UNTUK SATU PRODUK — satu-satunya jalan aturan merek sampai ke tangga
 * harga (pricing.ts membaca `brandPersen`, bukan peta mentah `brandDiscounts`).
 *
 *   · grup null → null (tamu / pelanggan umum: aturan merek milik LEVEL);
 *   · peta aturan belum dimuat (`brandDiscounts` absen) → grup apa adanya —
 *     termasuk `brandPersen` yang mungkin sudah diselesaikan pemanggil lain;
 *   · selain itu `brandPersen` dihitung ulang untuk produk ini dan SELALU
 *     terpasang eksplisit — `null` = merek ini tanpa aturan → tangga lama
 *     (override per-varian, lalu diskon rata). `undefined` khusus berarti
 *     "belum diselesaikan" (dibaca `levelForKnownProduct`), jadi grup hasil
 *     fungsi ini tak pernah membawanya.
 *
 * Objek yang sama dikembalikan bila hasilnya sudah terpasang, supaya
 * dependensi memo React tak berganti tiap render.
 */
export function levelForProduct(
  group: CustomerGroup,
  product: { brand?: string | null; isManual?: boolean | null } | null | undefined
): CustomerGroup {
  if (!group || group.brandDiscounts === undefined) return group;
  const persen = brandDiscountFor(group.brandDiscounts, product);
  if (group.brandPersen === persen) return group;
  return { ...group, brandPersen: persen };
}

/**
 * Level untuk satu produk yang MEREKNYA BELUM TENTU TERBACA (jalur batch:
 * Perbaiki Harga, tagihan massal). `undefined` = level ini punya aturan merek
 * tetapi produknya tak diketahui → TIDAK TAHU; pemanggil melewati barisnya,
 * bukan jatuh ke tangga lama (override per-varian bisa lebih dalam ATAU lebih
 * dangkal dari kesepakatan merek). Level tanpa aturan tak pernah terhalang.
 */
export function levelForKnownProduct(
  group: CustomerGroup,
  product: { brand?: string | null; isManual?: boolean | null } | null | undefined
): CustomerGroup | undefined {
  if (!group) return group;
  if (product) return levelForProduct(group, product);
  // Sudah diselesaikan pemuatnya untuk konteks SATU produk (mis.
  // `fetchClosedPOEditContext`) → pakai apa adanya.
  if (group.brandPersen !== undefined) return group;
  if (!group.brandDiscounts || group.brandDiscounts.size === 0) return group;
  return undefined;
}

/**
 * Persen aturan merek untuk SATU slot level & satu merek (daftar aturan
 * toko dari `group_brand_discount_rules()`), atau `null` — pratinjau form
 * produk yang mengenal level lewat slotnya, bukan id grup.
 */
export function brandPersenForSlot(
  rules: readonly GroupBrandRule[],
  slot: number,
  brand: string | null | undefined
): number | null {
  const bySlot = rules.filter((r) => r.slot === slot);
  if (!bySlot.length) return null;
  const map = brandDiscountsByGroup(bySlot);
  let best: number | null = null;
  for (const m of map.values()) {
    const p = brandDiscountFor(m, { brand });
    if (p !== null && (best === null || p > best)) best = p;
  }
  return best;
}

/**
 * Diskon untuk teks "Bagikan ke WhatsApp" satu level (bughunt D13-9): aturan
 * merek level itu (persen) bila ada — itulah yang ditagih katalog & checkout —
 * selain itu isian diskon per-produk (`fallback`). Penilainya sama dengan Form
 * Produk v1 (`brandPersenForSlot`), supaya siaran tak menjanjikan angka lain.
 */
export function brandAwareShareDiscount(
  rules: readonly GroupBrandRule[],
  slot: number,
  brand: string | null | undefined,
  fallback: { value: number; mode: "persen" | "nominal" }
): { value: number; mode: "persen" | "nominal" } {
  const persen = brandPersenForSlot(rules, slot, brand);
  return persen !== null ? { value: persen, mode: "persen" } : fallback;
}

/**
 * "<nama level> <persen>%" untuk level yang harganya dibentuk aturan merek —
 * isi catatan "Merek produk ini diatur di Grup Pelanggan (…)" di form produk
 * (v1, v2, Form Produk Cepat; bughunt D13-9).
 */
export function brandRuledLabels(
  groups: ReadonlyArray<{ name: string; brandRule?: number | null }>
): string[] {
  return groups
    .filter((g) => g.brandRule !== null && g.brandRule !== undefined)
    .map((g) => `${g.name} ${formatBrandPersen(g.brandRule as number)}%`);
}

/**
 * Level yang aturan mereknya SUDAH diketahui untuk satu produk — pratinjau
 * (form produk, editor promo) yang memegang persennya langsung dari daftar
 * aturan. `null` = merek itu tanpa aturan di level ini. Hasilnya hanya untuk
 * menghitung harga produk ITU: tanpa peta aturan, `levelForProduct` tak bisa
 * menyelesaikannya ulang untuk produk lain.
 */
export function levelWithBrandPersen(group: ResellerGroup, persen: number | null): ResellerGroup {
  return { ...group, brandPersen: persen };
}

/**
 * Persen aturan merek yang dipakai tangga harga untuk grup ini, atau `null`.
 * Angka mustahil (≤ 0, > 100, bukan angka) DITOLAK — jatuh ke tangga lama,
 * bukan harga Rp 0 (arah gagal yang sama dengan override per-varian).
 */
export function levelBrandPersen(group: CustomerGroup): number | null {
  const p = group?.brandPersen;
  if (p === undefined || p === null || !isUsableBrandPersen(p)) return null;
  return p;
}

// ─── Draf editor admin (Grup Pelanggan › Diskon per Merek) ───────────────────

/** Satu baris draf: merek terpilih + teks persen yang diketik admin. */
export type GroupBrandDraftRow = {
  brandId: string;
  brandName: string;
  persenText: string;
};

/** "40" / "12,5" / "12.5" → angka; kosong / bukan angka → null. */
export function parseBrandPersenInput(raw: string): number | null {
  const t = (raw ?? "").trim().replace(",", ".");
  if (!t || !/^\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Pesan galat draf pertama (Bahasa Indonesia), atau `null` bila sah. Cermin
 * validasi RPC `admin_set_group_brand_discounts` supaya admin membaca alasannya
 * sebelum Simpan, bukan galat server.
 */
export function validateGroupBrandDraft(rows: readonly GroupBrandDraftRow[]): string | null {
  const ids = new Set<string>();
  const names = new Map<string, string>();
  for (const [i, row] of rows.entries()) {
    const label = row.brandName?.trim() || `baris ${i + 1}`;
    if (!row.brandId) return `Pilih merek untuk ${label}.`;
    const persen = parseBrandPersenInput(row.persenText);
    if (persen === null) return `Isi diskon ${label} dengan angka (contoh 40 atau 12,5).`;
    if (!isUsableBrandPersen(persen)) {
      return `Diskon ${label} harus lebih dari 0 dan paling besar 100 (maksimal 2 desimal).`;
    }
    if (ids.has(row.brandId)) return `${label} muncul lebih dari sekali.`;
    ids.add(row.brandId);
    const key = normName(row.brandName);
    const twin = names.get(key);
    if (key && twin !== undefined) {
      return `${label} dan ${twin} bernama sama (beda huruf besar/spasi) — pilih salah satu.`;
    }
    names.set(key, label);
  }
  return null;
}

/** Draf sah → muatan RPC `p_rules`. Panggil SESUDAH `validateGroupBrandDraft` lolos. */
export function groupBrandRpcPayload(
  rows: readonly GroupBrandDraftRow[]
): { brandId: string; persen: number }[] {
  return rows.map((row) => ({
    brandId: row.brandId,
    persen: parseBrandPersenInput(row.persenText) ?? 0
  }));
}

/** Aturan tersimpan → baris draf editor (persen ditulis gaya Indonesia, koma desimal). */
export function draftRowsFromRules(rules: readonly GroupBrandRule[]): GroupBrandDraftRow[] {
  return rules.map((r) => ({
    brandId: r.brandId,
    brandName: r.brandName,
    persenText: formatBrandPersen(r.persen)
  }));
}

/** 40 → "40", 12.5 → "12,5". */
export function formatBrandPersen(value: number): string {
  if (!Number.isFinite(value)) return "";
  const rounded = Math.round(value * 100) / 100;
  return String(rounded).replace(".", ",");
}

/** Ringkasan perubahan draf terhadap aturan tersimpan — untuk konfirmasi Simpan. */
export function diffGroupBrandRules(
  saved: readonly GroupBrandRule[],
  next: readonly { brandId: string; persen: number }[]
): { added: string[]; changed: string[]; removed: string[] } {
  const before = new Map(saved.map((r) => [r.brandId, r.persen] as const));
  const after = new Map(next.map((r) => [r.brandId, r.persen] as const));
  const added: string[] = [];
  const changed: string[] = [];
  const removed: string[] = [];
  for (const [id, persen] of after) {
    const prev = before.get(id);
    if (prev === undefined) added.push(id);
    else if (Math.abs(prev - persen) > 1e-9) changed.push(id);
  }
  for (const id of before.keys()) if (!after.has(id)) removed.push(id);
  return { added, changed, removed };
}
